"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.monotoneCubicInterpolation = exports.CubicInterpolation = void 0;
class CubicInterpolation {
    firstX;
    lastX;
    firstY;
    lastY;
    firstSlope;
    lastSlope;
    fun;
    constructor(firstX, lastX, firstY, lastY, firstSlope, lastSlope, fun) {
        this.firstX = firstX;
        this.lastX = lastX;
        this.firstY = firstY;
        this.lastY = lastY;
        this.firstSlope = firstSlope;
        this.lastSlope = lastSlope;
        this.fun = fun;
    }
    forX(x) {
        if (x < this.firstX) {
            return this.firstY - this.firstSlope * (this.firstX - x);
        }
        else if (x > this.lastX) {
            return this.lastY + this.lastSlope * (x - this.lastX);
        }
        else {
            return this.fun(x);
        }
    }
    forY(y, precision) {
        if (y < this.firstY) {
            return this.firstX - (this.firstY - y) / this.firstSlope;
        }
        else if (y > this.lastY) {
            return this.lastX + (y - this.lastY) / this.lastSlope;
        }
        else {
            let lowX = this.firstX;
            let highX = this.lastX;
            let tries = 50;
            while (lowX <= highX && tries > 0) {
                const midX = (lowX + highX) / 2;
                if (Math.abs(highX - lowX) < precision) {
                    return midX;
                }
                const midY = this.forX(midX);
                if (Math.abs(midY - y) < precision) {
                    return midX;
                }
                if (midY < y) {
                    lowX = midX;
                }
                else {
                    highX = midX;
                }
                tries--;
            }
            throw new Error(`X was not found for Y = ${y.toString()}`);
        }
    }
}
exports.CubicInterpolation = CubicInterpolation;
const monotoneCubicInterpolation = (xs, ys) => {
    const { fun, firstSlope, lastSlope } = createInterpolant(xs, ys);
    return new CubicInterpolation(xs[0], xs[xs.length - 1], ys[0], ys[ys.length - 1], firstSlope, lastSlope, fun);
};
exports.monotoneCubicInterpolation = monotoneCubicInterpolation;
const createInterpolant = (xs, ys) => {
    if (xs.length != ys.length) {
        throw new Error("The number of xs and ys should be equal");
    }
    if (xs.length === 0) {
        throw new Error("Empty array of xs");
    }
    if (xs.length === 1) {
        throw new Error("Interpolation cannot be performed for a single point");
    }
    const [sortedXs, sortedYs] = sortPoints(xs, ys);
    if (!isStrictlyMonotonic(sortedYs)) {
        throw new Error("The given points are not monotonic");
    }
    const dys = [], dxs = [], ms = [];
    for (let i = 0; i < sortedXs.length - 1; i++) {
        const dx = sortedXs[i + 1] - sortedXs[i];
        const dy = sortedYs[i + 1] - sortedYs[i];
        dxs[i] = dx;
        dys[i] = dy;
        ms[i] = dy / dx;
    }
    const c1s = [ms[0]];
    for (let i = 0; i < dxs.length - 1; i++) {
        const m = ms[i], mNext = ms[i + 1];
        if (m * mNext <= 0) {
            c1s.push(0);
        }
        else {
            const dx = dxs[i], dxNext = dxs[i + 1], common = dx + dxNext;
            c1s.push((3 * common) / ((common + dxNext) / m + (common + dx) / mNext));
        }
    }
    c1s.push(ms[ms.length - 1]);
    const c2s = [], c3s = [];
    for (let i = 0; i < c1s.length - 1; i++) {
        const c1 = c1s[i], m = ms[i], invDx = 1 / dxs[i], common = c1 + c1s[i + 1] - 2 * m;
        c2s.push((m - c1 - common) * invDx);
        c3s.push(common * invDx * invDx);
    }
    return {
        fun: createInterpolantFunction(sortedXs, sortedYs, c1s, c2s, c3s),
        firstSlope: ms[0],
        lastSlope: ms[ms.length - 1],
    };
};
const sortPoints = (xs, ys) => {
    const indexes = [...Array(xs.length).keys()];
    indexes.sort((a, b) => {
        return xs[a] - xs[b];
    });
    const sortedXs = [];
    const sortedYs = [];
    for (let i = 0; i < xs.length; i++) {
        sortedXs[i] = xs[indexes[i]];
        sortedYs[i] = ys[indexes[i]];
    }
    return [sortedXs, sortedYs];
};
const isStrictlyMonotonic = (ys) => {
    if (ys.length < 2) {
        return true;
    }
    let direction = "undefined";
    let previous = ys[0];
    for (let i = 1; i < ys.length; i++) {
        const current = ys[i];
        if (current > previous) {
            if (direction === "decreasing") {
                return false;
            }
            direction = "increasing";
        }
        else if (current < previous) {
            if (direction === "increasing") {
                return false;
            }
            direction = "decreasing";
        }
        else {
            return false;
        }
        previous = current;
    }
    return true;
};
const createInterpolantFunction = (xs, ys, c1s, c2s, c3s) => (x) => {
    if (x < xs[0] || x > xs[xs.length - 1]) {
        throw new Error(`The function only handles arguments in the range [${xs[0].toString()},${xs[xs.length - 1].toString()}]`);
    }
    if (x === xs[xs.length - 1]) {
        return ys[xs.length - 1];
    }
    let low = 0, high = c3s.length - 1;
    while (low <= high) {
        const middle = Math.floor(0.5 * (low + high));
        const middleX = xs[middle];
        if (middleX < x) {
            low = middle + 1;
        }
        else if (middleX > x) {
            high = middle - 1;
        }
        else {
            return ys[middle];
        }
    }
    const i = Math.max(0, high);
    const diff = x - xs[i];
    return ys[i] + diff * (c1s[i] + diff * (c2s[i] + diff * c3s[i]));
};
//# sourceMappingURL=monotonic-cubic-spline.js.map